import { StateProvider } from '@uirouter/angularjs'
import angular from 'angular'

import { EVENTS, InsufficientStateForOfflineError } from '../constants'
import webregisterTemplate from '../../templates/webregister.html'
import { ROUTES, STATES } from '../../../lib/routes'
import { $state, $transitions } from '../ng-compat/imports'
import { UserManager } from '../managers/user-manager'
import {
  webRegisterStatusManager,
  WebRegisterStatusManager,
} from '../managers/web-register-status-manager'
import { PosStateManager } from '../managers/pos-state-manager'

/**
 * =======================================
 * ROUTES CONFIG
 * =======================================
 *
 * @module webregister.routes
 */
export function BaseRoutesConfig(
  $locationProvider: $TSFixMe,
  $stateProvider: StateProvider,
  $urlRouterProvider: $TSFixMe
) {
  // Use HTML5 History API
  $locationProvider.html5Mode(true)

  $stateProvider
    // All states inherit from this `webregister` state but it can never be explicitly navigated to (because it
    // is abstract).
    //
    // This ensures common prerequisites to all states are taken care of:
    //
    // - ensuring we have a logged in user
    // - initialising the various managers
    // - performing the initial sync
    // - prompting for register selection (if one not already selected)
    //
    .state(STATES.WEB_REGISTER, {
      abstract: true,
      template: webregisterTemplate,
      controller: 'WebRegisterCtrl',
      controllerAs: 'webRegisterCtrl',
      resolve: {
        errorHandler(redirectSigninOn401: $TSFixMe) {
          function redirectToOfflineErrorPage() {
            $state.go(STATES.ERROR_OFFLINE, { location: 'replace' })
          }

          $transitions.onError({}, transition => {
            const detail = transition.error().detail

            // Only redirect to offline error page if a 401 has not been encountered
            if (
              !redirectSigninOn401.hasEncountered401() &&
              detail &&
              detail instanceof InsufficientStateForOfflineError
            ) {
              redirectToOfflineErrorPage()
            }
          })

          webRegisterStatusManager.on(EVENTS.insufficientStateForOffline, () =>
            redirectToOfflineErrorPage()
          )
        },

        posStateManager: 'posStateManagerResolver' as $TSFixMe,

        // Ensure the error handler is initialised before we check the status
        webRegisterStatusManager(
          errorHandler: $TSFixMe,
          posStateManager: PosStateManager,
          webRegisterStatusManagerResolver: $TSFixMe
        ) {
          return webRegisterStatusManagerResolver
        },

        syncManager: 'syncManagerResolver' as $TSFixMe,
        saleSyncManager: 'saleSyncManagerResolver' as $TSFixMe,

        // Ensure we have a session before initialising the register manager as it makes a request to the API
        registerManager(
          webRegisterStatusManager: WebRegisterStatusManager,
          registerManagerResolver: $TSFixMe
        ) {
          return registerManagerResolver
        },

        // @todo this should really be a `loggedInUserResolver` but can't be because:
        //
        // - we can't let a request to /api/2.0/* trigger the 401 -> signin page redirect as it triggers
        //   the browser's built in username/password dialog for basic auth
        // - hence we need to depend on `webRegisterStatusManager` to ensure the request to /pos/status.json
        //   happens (and triggers a 401) first
        // - resolver interdependencies only seems to work for inline resolver functions - not external
        //   resolver services
        //
        // Once the /api/2.0/* no longer supports basic auth this can be refactored.
        //
        loggedInUser(
          webRegisterStatusManager: WebRegisterStatusManager,
          userManager: UserManager
        ) {
          return userManager.init().then(() => userManager.getLoggedInUser())
        },
      },
    })

  // Unmatched routes redirect to sell screen
  $urlRouterProvider.otherwise(ROUTES.SALE)
}

angular.module('webregister.routes').config(BaseRoutesConfig)
